import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Platform,
  TouchableOpacity,
  useWindowDimensions,
  Image,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../../store';
import {
  setHaulbackRcMap,
  setHaulbackDtoMap,
  setRoadblockRcMap,
  setRoadblockTypeMap,
  setCataloguesLanguage,
} from '../../store/slices/deliveryRpmSlice';
import { fetchHaulbacks, fetchRouteRoadblocks } from '../../api/deliveryRpmApi';
import { buildHaulbackReasonMaps, buildRoadblockReasonMaps } from './deliveryRpmReasonCodeUtils';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import { FontFamily } from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import { useDarkStyles } from '../../theme/useDarkStyles';
import OneWayScreen from './OneWayScreen';
import RouteRoadblocksScreen from './RouteRoadblocksScreen';
import StopIssueSubmitScreen from './StopIssueSubmitScreen';
import RouteIssueSubmitScreen from './RouteIssueSubmitScreen';
import SubmitHaulbackScreen from './SubmitHaulbackScreen';
import MyTruckScreen from './MyTruckScreen';

interface DeliveryRPMScreenProps {
  onNavigate: (screen: string, params?: { [key: string]: any }) => void;
}

// ─── Tab type ────────────────────────────────────────────────────────────────

type RpmTab = 'one-way' | 'route-roadblocks' | 'my-truck';
type RpmSubScreen = 'stop-issue-submit' | 'route-issue-submit' | 'haulback-submit' | null;
interface SubScreenParams { sscc?: string; proceedWhenNotScheduled?: boolean; customer?: { cof: string; name: string }; route?: string; date?: string; }

// ─── Tab icons ───────────────────────────────────────────────────────────────

const OneWayIcon = ({ active }: { active: boolean }) => (
  <Image
    source={require('../../assets/oneWayPng.png')}
    style={[baseStyles.tabIcon, !active && { opacity: 0.6 }]}
    resizeMode="contain"
  />
);

const RouteRoadblocksIcon = ({ active }: { active: boolean }) => (
  <Image
    source={require('../../assets/routeRodeblocksPng.png')}
    style={[baseStyles.tabIcon, !active && { opacity: 0.6 }]}
    resizeMode="contain"
  />
);

const MyTruckIcon = ({ active }: { active: boolean }) => (
  <Image
    source={require('../../assets/perfectTruckpng.png')}
    style={[baseStyles.tabIcon, !active && { opacity: 0.6 }]}
    resizeMode="contain"
  />
);

// ─── Tab config ──────────────────────────────────────────────────────────────

// ─── Tab config ──────────────────────────────────────────────────────────────
// Note: Icon and label configuration - labels will be translated in component

const TAB_CONFIG = [
  { key: 'one-way' as RpmTab, labelKey: 'delivery_rpm.tabs.one_way', Icon: OneWayIcon },
  { key: 'route-roadblocks' as RpmTab, labelKey: 'delivery_rpm.tabs.route_roadblocks', Icon: RouteRoadblocksIcon },
  { key: 'my-truck' as RpmTab, labelKey: 'delivery_rpm.tabs.my_truck', Icon: MyTruckIcon },
];

// ─── Main component ──────────────────────────────────────────────────────────

const DeliveryRPMScreen: React.FC<DeliveryRPMScreenProps> = ({ onNavigate }) => {
  const styles = useDarkStyles(baseStyles);
  const { t, i18n } = useTranslation('harbor');
  const currentLanguage = (i18n.language ?? 'en').split('-')[0];
  const [activeTab, setActiveTab] = useState<RpmTab>('one-way');
  const [subScreen, setSubScreen] = useState<RpmSubScreen>(null);
  const [subParams, setSubParams] = useState<SubScreenParams>({});
  const [roadblocksRefreshKey, setRoadblocksRefreshKey] = useState(0);
  const [roadblocksToastMessage, setRoadblocksToastMessage] = useState<string | null>(null);
  const [oneWayToastMessage, setOneWayToastMessage] = useState<string | null>(null);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  // Create tabs with translated labels
  const TABS = TAB_CONFIG.map(tab => ({
    ...tab,
    label: t(tab.labelKey),
  }));

  const dispatch = useDispatch<AppDispatch>();
  const userRoleName = useSelector(
    (state: RootState) => state.appData.userInfo?.roleName
  );
  const cataloguesLanguage = useSelector(
    (state: RootState) => state.deliveryRpm.cataloguesLanguage
  );

  // ── Load reason-code catalogues (translated for the current language) into Redux ──
  // Re-runs whenever the app language changes so the tables always show translated labels.
  useEffect(() => {
    if (cataloguesLanguage === currentLanguage) return;
    Promise.all([
      fetchHaulbacks({ limit: '200', language: currentLanguage }).catch(() => ({ items: [] as any[] })),
      fetchRouteRoadblocks({ limit: '200', language: currentLanguage }).catch(() => ({ items: [] as any[] })),
    ]).then(([haulbackRes, roadblockRes]) => {
      const { rcMap: hMap, dtoMap: hDtoMap } = buildHaulbackReasonMaps(haulbackRes.items);
      dispatch(setHaulbackRcMap(hMap));
      dispatch(setHaulbackDtoMap(hDtoMap));

      const { rcMap: rMap, typeMap: rTypeMap } = buildRoadblockReasonMaps(roadblockRes.items);
      dispatch(setRoadblockRcMap(rMap));
      dispatch(setRoadblockTypeMap(rTypeMap));

      dispatch(setCataloguesLanguage(currentLanguage));
    });
  }, [cataloguesLanguage, currentLanguage, dispatch]);

  const handleInternalNavigate = (screen: string, params?: Record<string, any>) => {
    if (screen === 'Delivery RPM Submit') {
      setSubParams(params ?? {});
      setSubScreen('haulback-submit');
    } else if (screen === 'StopIssueSubmit') {
      setSubParams(params ?? {});
      setSubScreen('stop-issue-submit');
    } else if (screen === 'RouteIssueSubmit') {
      setSubParams(params ?? {});
      setSubScreen('route-issue-submit');
    } else if (screen === 'RouteRoadblocks') {
      setSubScreen(null);
      setActiveTab('route-roadblocks');
      setRoadblocksRefreshKey(k => k + 1);
      setRoadblocksToastMessage(params?.toastMessage ?? null);
    } else if (screen === 'Delivery RPM') {
      setSubScreen(null);
      setActiveTab('one-way');
      setOneWayToastMessage(params?.toastMessage ?? null);
    } else {
      onNavigate(screen, params);
    }
  };

  const renderTabContent = () => {
    // Haulback submit — rendered over one-way tab
    if (subScreen === 'haulback-submit') {
      return (
        <SubmitHaulbackScreen
          onNavigate={handleInternalNavigate}
          route={{ params: subParams }}
        />
      );
    }

    // Sub-screens rendered over route-roadblocks tab
    if (activeTab === 'route-roadblocks') {
      if (subScreen === 'stop-issue-submit') {
        return <StopIssueSubmitScreen onNavigate={handleInternalNavigate} route={{ params: subParams }} />;
      }
      if (subScreen === 'route-issue-submit') {
        return <RouteIssueSubmitScreen onNavigate={handleInternalNavigate} route={{ params: subParams }} />;
      }
    }
    switch (activeTab) {
      case 'one-way':
        return (
          <OneWayScreen
            roleName={userRoleName}
            onNavigate={handleInternalNavigate}
            toastMessage={oneWayToastMessage}
            onToastShown={() => setOneWayToastMessage(null)}
          />
        );
      case 'route-roadblocks':
        return (
          <RouteRoadblocksScreen
            roleName={userRoleName}
            onNavigate={handleInternalNavigate}
            refreshKey={roadblocksRefreshKey}
            toastMessage={roadblocksToastMessage}
            onToastShown={() => setRoadblocksToastMessage(null)}
          />
        );
      case 'my-truck':
        return <MyTruckScreen onNavigate={handleInternalNavigate} />;
      default:
        return <OneWayScreen roleName={userRoleName} onNavigate={handleInternalNavigate} />;
    }
  };

  return (
    <View style={styles.root}>
      {!subScreen && (
        <>
          {/* ── Breadcrumb + Page Header ─────────────────────────────── */}
          <View style={styles.header}>
            <View style={styles.breadcrumb}>
              <TouchableOpacity onPress={() => onNavigate('Harbor')}>
                <Text style={styles.breadcrumbLink}>{t('breadcrumb_harbor')}</Text>
              </TouchableOpacity>
              <Text style={styles.breadcrumbSep}>{'>'}</Text>
              <Text style={styles.breadcrumbCurrent}>{t('breadcrumb_delivery_rpm')}</Text>
            </View>
            <View style={styles.headerTitleRow}>
              <Text style={styles.pageTitle}>{t('delivery_rpm.page_title')}</Text>
              <PTLanguageToggle compact={isMobile} />
            </View>
          </View>

          {/* ── Persistent Tab Bar ──────────────────────────────────── */}
          <View style={styles.tabsWrap}>
            <View style={styles.tabsContainer}>
              {TABS.map(tab => {
                const isActive = activeTab === tab.key;
                return (
                  <Pressable
                    key={tab.key}
                    style={[styles.tab, isActive && styles.tabActive]}
                    onPress={() => { setActiveTab(tab.key); setSubScreen(null); }}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: isActive }}
                    accessibilityLabel={tab.label}
                  >
                    <tab.Icon active={isActive} />
                    <Text
                      style={[styles.tabLabel, isActive && styles.tabLabelActive]}
                      numberOfLines={isMobile ? 2 : 1}
                    >
                      {tab.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </>
      )}

      {/* ── Tab Content ─────────────────────────────────────────── */}
      <View style={styles.content}>
        {renderTabContent()}
      </View>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const baseStyles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: AdminColors.bgPage,
  },
  header: {
    backgroundColor: AdminColors.bgPage,
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 0,
    flexShrink: 0,
  },
  headerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: AdminColors.blue700,
  },
  breadcrumbSep: {
    fontSize: 14,
    color: '#838383',
  },
  breadcrumbCurrent: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#2D2D2D',
    fontWeight: '500',
  },
  pageTitle: {
    fontSize: 28,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#2D2D2D',
    lineHeight: 40,
    marginBottom: 18,
  },
  tabsWrap: {
    paddingHorizontal: 24,
    flexShrink: 0,
    marginBottom: 20,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    ...Platform.select({
      ios: {
        shadowColor: '#0055BC',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.06,
        shadowRadius: 4,
      },
      android: { elevation: 1 },
    }),
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 11,
    paddingVertical: 13,
    paddingHorizontal: 14,
    backgroundColor: 'transparent',
    borderRadius: 12,
  },
  tabIcon: {
    width: 34,
    height: 34,
  },
  tabActive: {
    backgroundColor: '#14274E',
  },
  tabLabel: {
    fontSize: 15,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
    color: '#2D2D2D',
    flexShrink: 1,
  },
  tabLabelActive: {
    color: '#FFFFFF',
    fontFamily: FontFamily.bold,
    fontWeight: '700',
  },
  content: {
    flex: 1,
  },
});

export default DeliveryRPMScreen;
